import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Plus, RefreshCw } from 'lucide-react';
import { labels, request } from './editorial-client';
import { useEditorialQueue } from './studio-queue';
import { deskLane, selectedDeskLane } from './studio-model';
import StudioCover from './StudioCover';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import RejectArticleForm from './RejectArticleForm';
import EditQueuedTitle from './EditQueuedTitle';

const lanes = [
  ['review', 'Needs your eye'],
  ['preparing', 'In preparation'],
  ['scheduled', 'Approved & scheduled'],
  ['complete', 'Delivered'],
] as const;
export function StudioHeading({ title }: { title: string }) {
  return <h1>{title}</h1>;
}
export default function StudioDesk({
  calendar = false,
}: {
  calendar?: boolean;
}) {
  const queue = useEditorialQueue();
  const [params, setParams] = useSearchParams();
  const filter = selectedDeskLane(params.get('view'));
  const [search, setSearch] = useState('');
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectError, setRejectError] = useState('');
  const [rejectTarget,setRejectTarget]=useState<{id:string;title:string;retry:boolean;idea?:boolean}|null>(null);
  const [editTarget,setEditTarget]=useState<{id:string;title:string;token:string}|null>(null);
  const [startingEdit,setStartingEdit]=useState(false);
  async function editTitle(id:string,title:string) {
    setStartingEdit(true);setRejectError('');
    try {const {token}=await request<{token:string}>('POST',{action:'begin-title-edit',runId:id});setEditTarget({id,title,token});await queue.refetch();}
    catch(error){setRejectError(error instanceof Error?error.message:'Could not edit title.');await queue.refetch();}
    finally{setStartingEdit(false);}
  }
  async function replaceArticle(runId: string, reason: string) {
    setRejecting(runId); setRejectError('');
    try { await request('POST', {action:'reject-and-replace',runId,reason}); setRejectTarget(null); await queue.refetch(); }
    catch(error) { setRejectError(error instanceof Error ? error.message : 'Could not replace article.'); }
    finally { setRejecting(null); }
  }
  const runs = queue.data?.runs ?? [];
  const visible = runs
    .filter(
      (run) =>
        (calendar ? Boolean(run.publishAt) : deskLane(run) === filter) &&
        (run.topic?.title ?? run.requestedTitle ?? '').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) =>
      calendar
        ? Date.parse(a.publishAt ?? '') - Date.parse(b.publishAt ?? '')
        : filter==='preparing' ? Date.parse(a.createdAt??'')-Date.parse(b.createdAt??'') : 0
    );
  const routine = queue.data?.routines[0];
  return (
    <section>
      <header className="desk-heading">
        <StudioHeading title={calendar ? 'Calendar' : 'Review desk'} />
        <div className="desk-heading-actions">
          <button
            className="desk-button secondary"
            aria-label="Refresh editorial queue"
            disabled={queue.isFetching}
            onClick={() => void queue.refetch()}
          >
            <RefreshCw size={15} />
          </button>
          <Link className="desk-button" to="/create">
            <Plus size={15} /> Create article
          </Link>
        </div>
      </header>
      <div className="desk-routine-strip">
        <div>
          <strong>
            <span className="desk-dot" />
            {queue.data?.available && routine?.enabled
              ? `${routine.backlogLimit} queued ideas · 1 article for image review`
              : 'Automatic preparation is paused'}
          </strong>
        </div>
        <Link to="/automation">Manage routine</Link>
      </div>
      {queue.error && (
        <div className="desk-error" role="alert">
          Could not refresh the queue: {queue.error.message}.{' '}
          <button onClick={() => void queue.refetch()}>Try again</button>
        </div>
      )}
      <div className="desk-columns">
        <div>
          {!rejectTarget && rejectError && <p role="alert" className="desk-error">{rejectError}</p>}
          {!calendar && (
            <div className="desk-tabs" aria-label="Article stages">
              {lanes.map(([key, label]) => (
                <button
                  key={key}
                  aria-pressed={filter === key}
                  onClick={() => setParams({ view: key })}
                >
                  {label} ({runs.filter((run) => deskLane(run) === key).length})
                </button>
              ))}
            </div>
          )}
          <label>
            <span className="sr-only">Find an editorial article</span>
            <input
              className="desk-search"
              placeholder="Search articles…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          {queue.isPending ? (
            <div className="desk-empty" role="status">
              Loading articles…
            </div>
          ) : !visible.length ? (
            <div className="desk-empty">
              <h2>
                {search
                  ? 'No matching articles'
                  : calendar
                    ? 'No scheduled articles'
                    : 'No articles here'}
              </h2>
              <Link to="/articles" className="desk-button secondary mt-5">
                Browse article library <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="desk-list">
              {visible.map((run) => (
                <article className="desk-card" key={run.id}>
                  {run.articleId &&
                    !['topic', 'structure'].includes(run.stage) && (
                      <StudioCover articleId={run.articleId} />
                    )}
                  <div className="desk-card-body">
                    <div className="desk-card-top">
                      <span
                        className={`desk-status ${run.stage === 'attention' ? 'attention' : ''}`}
                      >
                        {['topic','structure','images'].includes(run.stage) && run.preparationOwner === false
                          ? 'Queued'
                          : run.stage === 'topic' && !run.preparationActive ? 'Queued' : labels[run.stage] ?? run.stage}
                      </span>
                    </div>
                    <h2>{run.topic?.title ?? run.requestedTitle ?? (run.stage === 'attention' ? 'Topic preparation failed' : run.stage==='planning' ? 'Choosing a topic' : 'Topic queued')}</h2>
                    {run.stage==='queued' && <p>Ready to generate when the image-review slot is free.</p>}
                    {run.lastErrorCode && <p>
                      {run.lastErrorCode
                        ? run.lastErrorCode === 'HEADINGS_REQUIRE_EDITORIAL_ATTENTION' && run.headingReviewReason
                          ? `Automatic heading revisions exhausted: ${run.headingReviewReason}`
                          : ['EDITORIAL_KNOWLEDGE_NOT_READY', 'EDITORIAL_LIBRARY_REFRESH_REQUIRED'].includes(run.lastErrorCode)
                          ? 'Waiting for the editorial guide refresh. Preparation resumes automatically.'
                          : run.lastErrorCode === 'IMAGES_REQUIRE_ATTENTION'
                          ? 'Some images failed. Retry in image review.'
                          : `Action needed: ${run.lastErrorCode.replace(/_/g, ' ').toLowerCase()}`
                        : null}
                    </p>}
                  </div>
                  <div className="desk-card-footer">
                    {queue.data?.available && !run.articleId && run.stage==='queued' && <button className="desk-button secondary" disabled={startingEdit||Boolean(rejecting)||Boolean(run.preparationActive)} onClick={()=>void editTitle(run.id,run.topic?.title??'')}>Edit title</button>}
                    {queue.data?.available && !run.approvedAt && !run.publishAt && (['review','attention'].includes(run.stage)||!run.articleId&&['queued','planning'].includes(run.stage)) && <button className="desk-button secondary" disabled={rejecting !== null||Boolean(run.preparationActive)} onClick={()=>{setRejectError('');setRejectTarget({id:run.id,title:run.topic?.title??run.requestedTitle??'Idea',idea:!run.articleId,retry:Boolean(run.rejectionPending || run.lastErrorCode?.startsWith('REJECTED_'))});}}>{rejecting===run.id?'Replacing…':run.rejectionPending || run.lastErrorCode?.startsWith('REJECTED_')?'Retry deletion & replacement':'Reject & replace'}</button>}
                    <span>
                      {run.publishAt
                        ? new Date(run.publishAt).toLocaleString()
                        : 'Not scheduled'}
                    </span>
                    {run.articleId && (
                      <Link to={`/review/${encodeURIComponent(run.articleId)}`}>
                        {['review', 'attention'].includes(run.stage)
                          ? 'Review images'
                          : 'View progress'}{' '}
                        <ArrowRight size={15} />
                      </Link>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
      <Dialog open={Boolean(rejectTarget)} onOpenChange={open=>{if(!open && !rejecting)setRejectTarget(null);}}>
        <DialogContent className="max-w-lg">
          <DialogTitle>Reject & replace</DialogTitle>
          <p className="text-sm font-medium">{rejectTarget?.title}</p>
          <DialogDescription>{rejectTarget?.idea?'Discard this queued idea and prepare a different one. No article or images have been generated.':'Permanently delete this unpublished article and its images, then prepare a replacement. This cannot be undone.'}</DialogDescription>
          {rejectTarget && <RejectArticleForm key={rejectTarget.id} busy={Boolean(rejecting)} error={rejectError} retry={rejectTarget.retry} idea={rejectTarget.idea} onCancel={()=>setRejectTarget(null)} onSubmit={reason=>void replaceArticle(rejectTarget.id,reason)} />}
        </DialogContent>
      </Dialog>
      {editTarget&&<EditQueuedTitle target={editTarget} onClose={()=>setEditTarget(null)} onSaved={()=>queue.refetch()} />}
    </section>
  );
}
